import React, { useMemo, useState } from 'react';
import { PersonDto } from '../services/api';
import { Button, ModalFooter, ModalHeader, Select, TextInput } from './common';
import { getPersonDisplayName } from '../utils/relationshipLabels';
import { useTranslation } from 'react-i18next';

interface ExistingPersonPickerModalProps {
  currentPerson: PersonDto;
  people: PersonDto[];
  onClose: () => void;
  onSelect: (selection: { relatedPersonId: string; relationshipType: string }) => void;
}

const buildPersonMeta = (person: PersonDto) => {
  const details: string[] = [];

  if (person.gender) {
    details.push(person.gender);
  }

  if (person.dateOfBirth) {
    details.push(`Born ${new Date(person.dateOfBirth).getUTCFullYear()}`);
  }

  return details.join(' • ');
};

export const ExistingPersonPickerModal: React.FC<ExistingPersonPickerModalProps> = ({
  currentPerson,
  people,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const RELATIONSHIP_TYPE_OPTIONS = [
    { value: 'parent', label: t('relationship.types.parent', { ns: 'canvas' }) },
    { value: 'stepFather', label: t('relationship.types.stepFather', { ns: 'canvas' }) },
    { value: 'stepMother', label: t('relationship.types.stepMother', { ns: 'canvas' }) },
    { value: 'child', label: t('relationship.types.child', { ns: 'canvas' }) },
    { value: 'spouse', label: t('relationship.types.spouse', { ns: 'canvas' }) },
    { value: 'sibling', label: t('relationship.types.sibling', { ns: 'canvas' }) },
    { value: 'stepSibling', label: t('relationship.types.stepSibling', { ns: 'canvas' }) },
    { value: 'aunt', label: t('relationship.types.aunt', { ns: 'canvas' }) },
    { value: 'uncle', label: t('relationship.types.uncle', { ns: 'canvas' }) },
    { value: 'cousin', label: t('relationship.types.cousin', { ns: 'canvas' }) },
  ];
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipType, setRelationshipType] = useState('parent');

  const filteredPeople = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return people
      .filter((person) => person.id !== currentPerson.id)
      .sort((left, right) => getPersonDisplayName(left).localeCompare(getPersonDisplayName(right)))
      .filter((person) => {
        if (!normalizedSearch) {
          return true;
        }

        const searchableText = [
          getPersonDisplayName(person),
          person.gender ?? '',
          person.placeOfBirth ?? '',
          person.placeOfDeath ?? '',
          person.restingPlace ?? '',
        ]
          .join(' ')
          .toLowerCase();

        return searchableText.includes(normalizedSearch);
      });
  }, [currentPerson.id, people, searchTerm]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,23,20,0.45)] px-4 py-6" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
        onClick={(event) => event.stopPropagation()}
      >
        <ModalHeader
          eyebrow={t('existingPersonPicker.eyebrow', { ns: 'canvas' })}
          title={t('existingPersonPicker.title', { ns: 'canvas' })}
          description={t('existingPersonPicker.description', { ns: 'canvas' })}
          onClose={onClose}
          closeAriaLabel={t('existingPersonPicker.closeAriaLabel', { ns: 'canvas' })}
        />

        <div className="border-b border-[#E2DDD8] bg-[linear-gradient(135deg,#EEF4FB_0%,#F6FAFE_100%)] px-6 py-4">
          <div className="grid gap-4 md:grid-cols-[1.4fr_0.9fr]">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">{t('existingPersonPicker.searchLabel', { ns: 'canvas' })}</span>
              <TextInput
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('existingPersonPicker.searchPlaceholder', { ns: 'canvas' })}
                className="bg-[#FFFFFF]"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">{t('existingPersonPicker.relationshipTypeLabel', { ns: 'canvas' })}</span>
              <Select
                value={relationshipType}
                onChange={(event) => setRelationshipType(event.target.value)}
                aria-label={t('existingPersonPicker.relationshipTypeLabel', { ns: 'canvas' })}
                options={RELATIONSHIP_TYPE_OPTIONS}
                className="bg-[#FFFFFF]"
              />
            </label>
          </div>

          <div className="mt-4 rounded-2xl border border-[#D8E4F2] bg-[#FFFFFF] px-4 py-3 text-sm text-[#4A5565]">
            {t('existingPersonPicker.linkingFrom', { ns: 'canvas' })} <span className="font-semibold text-[#1A1714]">{getPersonDisplayName(currentPerson)}</span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {filteredPeople.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#D7D3CC] bg-[#F9F7F4] px-5 py-8 text-center text-sm text-[#6B6560]">
              {t('existingPersonPicker.noResults', { ns: 'canvas' })}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPeople.map((person) => {
                const displayName = getPersonDisplayName(person);
                const personMeta = buildPersonMeta(person);

                return (
                  <Button
                    key={person.id}
                    type="button"
                    onClick={() =>
                      onSelect({
                        relatedPersonId: person.id,
                        relationshipType,
                      })
                    }
                    variant="outline"
                    className="flex h-auto w-full items-center justify-between gap-4 rounded-2xl border-[#E2DDD8] bg-[#FFFFFF] px-4 py-4 text-left font-normal hover:border-[#B9D0EB] hover:bg-[#F8FBFE]"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-[#1A1714]">{displayName}</p>
                      <p className="mt-1 text-sm text-[#6B6560]">{personMeta || t('existingPersonPicker.noExtraDetails', { ns: 'canvas' })}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-[#185FA5] px-3 py-1.5 text-xs font-medium text-white">
                      {t('existingPersonPicker.selectButton', { ns: 'canvas' })}
                    </span>
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            {t('person.backToProfile', { ns: 'canvas' })}
          </Button>
        </ModalFooter>
      </div>
    </div>
  );
};
